import {
  IPdfDefinitions,
  IPdfData,
  PdfContentType,
  PdfContentSetting,
  IPdfBulkOptions,
  CHARGEABLE_TIPS_DEFINE,
} from '@app/shared/common/make-pdf/v2';
import { Content } from 'pdfmake/interfaces';
import { STAND_WORKSHEET_PDF_HEADER_VALUES } from '@app/shared/types/enums/stand-worksheet-pdf.enum';
import { isNil } from 'lodash';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

const buildOpenSheetPdfConfig = (
  data: IPdfData,
  bulkOptions: IPdfBulkOptions,
  i18nService: XVII18nService,
): IPdfDefinitions => ({
  data,
  bulkOptions,
  generateFileName: (): string => '',
  generateHeader: (pdfData: IPdfData): Content[] => {
    return [
      {
        text: i18nService.instant(
          I18n.Forms.StandWorksheetForm.StandWorksheetOpening,
        ),
        fontSize: 14,
        bold: true,
        margin: [20, 20, 20, 0],
      },
      {
        fontSize: 8,
        margin: [20, 5, 20, 0],
        columns: [
          {
            text: [
              { text: i18nService.instant(I18n.Forms.GridForm.EventWithColon) },
              {
                text: pdfData.event_name,
                bold: true,
              },
            ],
            width: '33%',
          },
          {
            text: [
              {
                text: i18nService.instant(
                  I18n.Forms.GridForm.EventStartWithColon,
                ),
              },
              {
                text: pdfData.event_start_date,
                bold: true,
              },
            ],
            width: '34%',
          },
          {
            text: [
              {
                text: i18nService.instant(
                  I18n.Forms.GridForm.ChargeableWithColon,
                ),
              },
              {
                text: i18nService.instant(pdfData.chargeable),
                bold: true,
              },
            ],
            width: '33%',
          },
        ],
      },
      {
        fontSize: 8,
        margin: [20, 5, 20, 0],
        columns: [
          {
            text: [
              {
                text: i18nService.instant(I18n.Forms.GridForm.SiteWithColon),
              },
              {
                text: pdfData.site,
                bold: true,
              },
            ],
            width: '33%',
          },
          {
            text: [
              {
                text: i18nService.instant(
                  I18n.Forms.GridForm.EventEndWithColon,
                ),
              },
              {
                text: pdfData.event_end_date,
                bold: true,
              },
            ],
            width: '34%',
          },
        ],
      },
    ];
  },
  content: {
    tips: CHARGEABLE_TIPS_DEFINE,
    mainGrids: {
      type: PdfContentType.Grids,
      setting: (): PdfContentSetting => {
        return {
          items: {
            columnDefaultStyle: {
              style: {
                fontSize: 8,
              },
            },
            columns: [
              {
                path: 'chargeable',
                title: '',
                width: '1%',
                valueGetter(params): string {
                  return params.value ? '$' : '';
                },
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.ItemName,
                title: i18nService.instant(I18n.Forms.GridForm.ItemName),
                width: '35%',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.ProductPrice,
                title: i18nService.instant(I18n.EntityNames.ProductPrice),
                width: `8.5%`,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string => {
                  return tools.services.numberFormatService.getNumberFormatter(
                    true,
                  )(params);
                },
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.BeginningCount,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.BeginCount,
                ),
                width: `7%`,
                alignment: 'right',
                valueFormatter: ({ tools, ...params }): string => {
                  return isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                      )(params);
                },
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.MGRStartingOverride,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.MGRStartingOverride,
                ),
                width: `7%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventIn,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.DuringEventIn,
                ),
                width: `7%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventOut,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.DuringEventOut,
                ),
                width: `7%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.Gratis,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.Gratis,
                ),
                width: `7%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.IngredientSpoil,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.Spoilage,
                ),
                width: `7%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.Adjustment,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.Adjustment,
                ),
                width: `7.5%`,
                alignment: 'right',
              },
              {
                path: STAND_WORKSHEET_PDF_HEADER_VALUES.EndingCount,
                title: i18nService.instant(
                  I18n.Forms.StandWorksheetForm.EndingCount,
                ),
                width: `7%`,
                alignment: 'right',
              },
            ],
            height: 20,
          },
        };
      },
    },
    signatures: {
      type: PdfContentType.Signatures,
      setting: [
        i18nService.instant(I18n.Forms.StandWorksheetForm.BeginCount),
        i18nService.instant(I18n.Forms.StandWorksheetForm.AuditSignature),
        i18nService.instant(I18n.Forms.StandWorksheetForm.EndingCount),
        i18nService.instant(
          I18n.Forms.StandWorksheetForm.StandSupervisorSignature,
        ),
      ],
    },
  },
  options: {
    pageMargins: [20, 80, 20, 60],
    gridHeaderFontSize: 8,
  },
});

export { buildOpenSheetPdfConfig };
